import type {
  CcUsageGranularity,
  CcUsageQueryParams,
  CcUsageRange,
  CcUsageStackedSeries,
  CcUsageStackedTimeseriesRow,
  CcUsageTimeseriesByGroupResponse,
  CcUsageTimeseriesParams,
  CcUsageTimeseriesPoint,
  CcUsageTimeseriesResponse,
  CcUsageTokens,
} from '../types/api.js';

/**
 * range プリセットに対応する最古 bucket (JST 日付, 含む) を算出する。
 *
 * `today - (d - 1)` を返すのは、backend の `resolvePeriod`
 * (`shared/utils/jst-period.ts`) が preset range を「当日を含む JST カレンダー d 日」と
 * 定義しているため。`today - d` にすると 365d プリフェッチからの導出値だけ 1 バケット多くなり、
 * 同一画面でサーバー直フェッチのサマリーカードとグラフの合計が 1 日ぶん食い違う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business JST 基準で range プリセットに対応する from 日付文字列を算出 (当日を含む d 日)
 * @graph-connects none
 */
function resolveFromBucket(range: CcUsageRange): string {
  const now = new Date();
  const jstMs = now.getTime() + 9 * 60 * 60 * 1000;
  const jstToday = new Date(jstMs);
  jstToday.setUTCHours(0, 0, 0, 0);

  const days: Record<string, number> = {
    '7d': 7,
    '14d': 14,
    '30d': 30,
    '90d': 90,
    '180d': 180,
    '365d': 365,
  };
  const d = days[range];
  if (d === undefined) throw new Error(`Cannot resolve from for range=${range}`);

  jstToday.setUTCDate(jstToday.getUTCDate() - (d - 1));
  return formatDate(jstToday);
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Date を YYYY-MM-DD 文字列に変換
 * @graph-connects none
 */
function formatDate(d: Date): string {
  const y = String(d.getUTCFullYear());
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 日付文字列を月曜始まりの週バケットに変換
 * @graph-connects none
 */
function getMondayOf(dateStr: string): string {
  const parts = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(parts[0] ?? 0, (parts[1] ?? 1) - 1, parts[2]));
  const day = date.getUTCDay();
  const diff = day === 0 ? 6 : day - 1;
  date.setUTCDate(date.getUTCDate() - diff);
  return formatDate(date);
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 日付文字列を月初バケットに変換
 * @graph-connects none
 */
function truncToMonth(dateStr: string): string {
  return dateStr.slice(0, 7) + '-01';
}

/** @graph-connects none */
function emptyTokens(): CcUsageTokens {
  return {
    inputTokens: 0,
    outputTokens: 0,
    cacheCreationInputTokens: 0,
    cacheReadInputTokens: 0,
  };
}

/** @graph-connects none */
function addTokens(a: CcUsageTokens, b: CcUsageTokens): CcUsageTokens {
  return {
    inputTokens: a.inputTokens + b.inputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
    cacheCreationInputTokens: a.cacheCreationInputTokens + b.cacheCreationInputTokens,
    cacheReadInputTokens: a.cacheReadInputTokens + b.cacheReadInputTokens,
  };
}

type BucketFn = (dateStr: string) => string;

/** @graph-connects none */
function bucketFnFor(granularity: CcUsageGranularity): BucketFn {
  if (granularity === 'week') return getMondayOf;
  if (granularity === 'month') return truncToMonth;
  return (d) => d;
}

/**
 * 365d/day の全データから指定 range で rows をフィルタし、
 * granularity に応じて再バケット化して返す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business max-range データから任意の range/granularity の stacked timeseries を導出
 * @graph-connects none
 */
function deriveTimeseriesByGroup(
  maxData: CcUsageTimeseriesByGroupResponse,
  range: CcUsageRange,
  granularity: CcUsageGranularity,
): CcUsageTimeseriesByGroupResponse {
  const from = resolveFromBucket(range);
  const filtered = maxData.rows.filter((r) => r.bucket >= from);

  const toBucket = bucketFnFor(granularity);
  const seriesKeys = maxData.series.map((s) => s.key);

  const bucketMap = new Map<string, Record<string, CcUsageTokens>>();
  for (const row of filtered) {
    const bk = toBucket(row.bucket);
    let entry = bucketMap.get(bk);
    if (!entry) {
      entry = {};
      bucketMap.set(bk, entry);
    }
    for (const key of seriesKeys) {
      const val = row.values[key];
      if (!val) continue;
      entry[key] = entry[key] ? addTokens(entry[key], val) : { ...val };
    }
  }

  const rows: CcUsageStackedTimeseriesRow[] = [...bucketMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucket, values]) => ({ bucket, values }));

  const seriesTotals = new Map<string, CcUsageTokens>();
  for (const row of rows) {
    for (const key of seriesKeys) {
      const val = row.values[key];
      if (!val) continue;
      const cur = seriesTotals.get(key);
      seriesTotals.set(key, cur ? addTokens(cur, val) : { ...val });
    }
  }

  const series: CcUsageStackedSeries[] = maxData.series
    .map((s) => ({
      key: s.key,
      label: s.label,
      tokens: seriesTotals.get(s.key) ?? emptyTokens(),
    }))
    .filter((s) => {
      const t = s.tokens;
      return (
        t.inputTokens > 0 ||
        t.outputTokens > 0 ||
        t.cacheCreationInputTokens > 0 ||
        t.cacheReadInputTokens > 0
      );
    });

  return { groupBy: maxData.groupBy, series, rows };
}

/**
 * 365d/day の stacked データから simple timeseries を導出する。
 * 全 group の token を bucket ごとに合算し、messageCount は含めない
 * （stacked レスポンスに messageCount がないため）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business max-range stacked データから simple timeseries を導出
 * @graph-connects none
 */
function deriveTimeseries(
  maxData: CcUsageTimeseriesByGroupResponse,
  range: CcUsageRange,
  granularity: CcUsageGranularity,
): CcUsageTimeseriesResponse {
  const from = resolveFromBucket(range);
  const filtered = maxData.rows.filter((r) => r.bucket >= from);

  const toBucket = bucketFnFor(granularity);

  const bucketMap = new Map<string, CcUsageTokens>();
  for (const row of filtered) {
    const bk = toBucket(row.bucket);
    let agg = bucketMap.get(bk) ?? emptyTokens();
    for (const val of Object.values(row.values)) {
      agg = addTokens(agg, val);
    }
    bucketMap.set(bk, agg);
  }

  const data: CcUsageTimeseriesPoint[] = [...bucketMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucket, tokens]) => ({
      bucket,
      ...tokens,
      messageCount: 0,
    }));

  return { data };
}

/** @graph-connects none */
const MAX_RANGE_PRESETS = new Set<CcUsageRange>(['7d', '14d', '30d', '90d', '180d', '365d']);

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage クエリの有効性判定
 * @graph-connects none
 */
function isCcUsageParamsValid(params: CcUsageQueryParams | undefined): boolean {
  if (!params) return true;
  if (params.scope === 'team') return Boolean(params.team);
  if (params.scope === 'member') return Boolean(params.email);
  return true;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business max-range プリフェッチ適用可否判定
 * @graph-connects none
 */
function canUseMaxRange(params: CcUsageTimeseriesParams): boolean {
  if (params.scope === 'member') return false;
  const range = params.range ?? '30d';
  return MAX_RANGE_PRESETS.has(range);
}

export {
  deriveTimeseriesByGroup,
  deriveTimeseries,
  resolveFromBucket,
  getMondayOf,
  truncToMonth,
  MAX_RANGE_PRESETS,
  isCcUsageParamsValid,
  canUseMaxRange,
};
